import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksToggleChangeEvent } from '../../events/blocks-toggle-change';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../skeleton/skeleton';
import styles from './toggle.styles';

/**
 * @slot on - Custom on icon for custom off state
 * @slot off - Custom off icon for custom off state
 * @slot unchecked-label - un checked label slot
 * @slot checked-label - Checked label slot
 * @slot field-note - field-note slot
 * @fires bl-toggle-change - Emitted when a element toggled
 * @csspart base - The component's base wrapper.
 * @csspart skeleton-label - The component's skeleton for label.
 * @csspart skeleton-wrapper - The component's skeleton wrapper.
 * @csspart skeleton-body - The component's skeleton body.
 * @csspart skeleton-first - The component's skeleton-first wrapper.
 * @csspart skeleton-second - The component's skeleton-second wrapper.
 * @csspart skeleton-field-note - The component's skeleton field-note.
 * @csspart part-label - The component's label wrapper.
 * @csspart input - The component's input element.
 * @csspart wrapper - The component's wrapper.
 * @csspart checkbox - The component's input.
 * @csspart handle - The component's handle wrapper.
 * @csspart on-wrap - The component's on-wrap element.
 * @csspart part-on - The component's on slot.
 * @csspart off-wrap - The component's off-wrap element.
 * @csspart part-off - The component's off slot.
 * @csspart value - The component's value.
 * @csspart part-checked-label - The component's checked-label slot.
 * @csspart part-unchecked-label - The component's unchecked-label slot.
 * @csspart field-note-wrap - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 */
export class BlocksToggle extends BlocksElement {
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * Disabled state
   * - If true, makes the checkbox within the toggle disabled
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * readonly state
   * - When true, disables user interaction with the toggle. The toggle appears visually as read-only and cannot be toggled.
   */
  @property({ type: Boolean })
  readonly: boolean | undefined;

  /**
   * Checked state
   * - If true, sets toggle the checkbox to active
   * - If false, sets toggle the checkbox to active
   */
  @property({ type: Boolean })
  checked = false;

  /**
   * Aria describedby
   * 1) Used to connect the field note for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Field ID
   * - Links the label to the checkbox
   * - By default it is autogenerated by nanoid
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * Label - Displayed above toggle component
   */
  @property({ type: String })
  label = 'Toggle';

  /**
   * Hide Label
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * Name attribute
   * - The name attribute used on the checkbox
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Value attribute
   * - The value attribute used on the checkbox
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Description  for the field
   */
  @property({ type: String })
  fieldNote: string | undefined;

  protected slots = new SlotController(this, 'on', 'off', 'unchecked-label', 'checked-label', 'field-note');

  private internals_ = this.attachInternals?.();
  private valueLabelId?: string;

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }

  private get hasUncheckedLabelSlot(): boolean {
    return this.slots.hasSlotted('unchecked-label');
  }

  private get hasCheckedLabelSlot(): boolean {
    return this.slots.hasSlotted('checked-label');
  }

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  /**
   * Connected callback lifecycle
   * 1) Autogenerates ids to make this accessible if these properties aren't provided
   */
  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid(); /* 1 */
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('value')) {
      this.internals_?.setFormValue(this.checked ? String(this.checked) : String(false));
    }
  }

  /**
   * Trigger toggle event
   */
  private triggerToggleEvent() {
    this.checked = !this.checked;
    this.shadowRoot?.querySelector<HTMLInputElement>('.cat-c-toggle__checkbox')?.focus();
    this.dispatch({ eventName: 'bl-toggle-change', detailObj: { checked: this.checked } }) as BlocksToggleChangeEvent;
  }

  /**
   * Handle on keydown events
   * 1) If the Enter key is pressed, then check the checkbox
   */
  private handleKeydown(e: KeyboardEvent) {
    if (this.readonly && e.key !== KEYS.TAB) {
      e.preventDefault();
      return;
    }
    if (e.key === KEYS.ENTER) {
      this.triggerToggleEvent();
    }
  }

  private hasFieldNote() {
    return this.fieldNote || this.hasFieldNoteSlot;
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
    if (this.hasCheckedLabelSlot || this.hasUncheckedLabelSlot) {
      this.valueLabelId = nanoid();
    }
  }

  private handleAriaDescribedBy() {
    if (this.valueLabelId && this.ariaDescribedBy) {
      return this.valueLabelId + ' ' + this.ariaDescribedBy;
    } else if (this.valueLabelId || this.ariaDescribedBy) {
      return this.valueLabelId || this.ariaDescribedBy;
    } else {
      return nothing;
    }
  }

  /**
   * Render Loading State
   */
  private renderLoading(): TemplateResult<1> {
    return html`<div part="base" class="cat-c-toggle__loader">
      ${!this.hideLabel
        ? html`<cat-skeleton
            part="skeleton-label"
            exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
            width="100px"
            arialabel=""
          ></cat-skeleton>`
        : nothing}
      <div part="skeleton-wrapper" class="cat-c-toggle__loader-wrapper">
        <div part="skeleton-body" class="cat-c-toggle__loader-body">
          <cat-skeleton part="skeleton-first" exportparts=${this.getExportParts('skeleton-first', 'skeleton')} width="50px"></cat-skeleton>
          <cat-skeleton
            part="skeleton-second"
            exportparts=${this.getExportParts('skeleton-second', 'skeleton')}
            width="42px"
            arialabel=""
          ></cat-skeleton>
        </div>
        ${this.fieldNote || this.hasFieldNoteSlot
          ? html`<cat-skeleton
              part="skeleton-field-note"
              exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
              width="100px"
              arialabel=""
            ></cat-skeleton>`
          : nothing}
      </div>
    </div>`;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-toggle', {
      'cat-is-checked': this.checked === true,
      'cat-is-disabled': this.disabled === true,
      'cat-is-readonly': this.readonly === true,
      'cat-c-toggle-hidden--label': this.hideLabel,
      'cat-c-toggle--has-field-note': this.fieldNote || this.hasFieldNoteSlot,
      'cat-c-toggle--has-on': this.slots.hasSlotted('on'),
      'cat-c-toggle--has-off': this.slots.hasSlotted('off'),
      'cat-c-toggle--has-checked-label': this.hasCheckedLabelSlot,
      'cat-c-toggle--has-unchecked-label': this.hasUncheckedLabelSlot
    });

    if (this.isLoading) {
      return this.renderLoading();
    }

    this.setAriaDescribedBy();

    return html`<div part="base" class="${componentClassNames}">
      <label part="part-label" class="cat-c-toggle-label" for=${ifDefined(this.fieldId)}> ${this.label} </label>
      <div part="wrapper" class="cat-c-toggle-wrapper">
        <input
          part="input"
          class="cat-c-toggle__checkbox"
          type="checkbox"
          id="${ifDefined(this.fieldId)}"
          role="switch"
          aria-describedby="${ifDefined(this.handleAriaDescribedBy())}"
          aria-checked="${this.checked}"
          aria-readonly="${ifDefined(this.readonly)}"
          aria-disabled="${ifDefined(this.readonly)}"
          name="${ifDefined(this.name)}"
          value="${ifDefined(this.value)}"
          .checked=${this.checked}
          ?disabled=${this.disabled}
          @change=${this.triggerToggleEvent}
          @keydown=${this.handleKeydown}
        />
        <div part="handle" class="cat-c-toggle__handle" @click=${this.triggerToggleEvent}>
          <span part="on-wrap" class="cat-c-toggle__on" aria-hidden="${true}">
            <slot part="part-on" name="on"></slot>
          </span>
          <span part="off-wrap" class="cat-c-toggle__off" aria-hidden="${true}">
            <slot part="part-off" name="off"></slot>
          </span>
        </div>
        <span part="value" class="cat-c-toggle__value cat-c__label--checked" id=${ifDefined(this.checked ? this.valueLabelId : undefined)}>
          <slot part="part-checked-label" name="checked-label"></slot>
        </span>
        <span part="value" class="cat-c-toggle__value cat-c__label--unchecked" id=${ifDefined(!this.checked ? this.valueLabelId : undefined)}>
          <slot part="part-unchecked-label" name="unchecked-label"></slot>
        </span>
      </div>
      <cat-field-note
        part="field-note-wrap"
        exportparts=${this.getExportParts('field-note')}
        ?isDisabled=${this.disabled}
        id=${ifDefined(this.ariaDescribedBy)}
      >
        <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
      </cat-field-note>
    </div>`;
  }
}

if (customElements.get('cat-toggle') === undefined) {
  customElements.define('cat-toggle', BlocksToggle);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-toggle': BlocksToggle;
  }
}
